import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vouchersApi, departmentsApi, usersApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Search, FileText, Eye, Paperclip, Filter, X, CalendarIcon, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'HOLD', label: 'On Hold' },
  { value: 'PENDING_ADDITIONAL_APPROVAL', label: 'Pending 2nd Manager' },
  { value: 'MANAGER_APPROVED', label: 'Manager Approved' },
  { value: 'MANAGER_REJECTED', label: 'Manager Rejected' },
  { value: 'ADDITIONAL_MANAGER_REJECTED', label: '2nd Manager Rejected' },
  { value: 'INTERNAL_AUDIT_APPROVED', label: 'Audit Approved' },
  { value: 'INTERNAL_AUDIT_REJECTED', label: 'Audit Rejected' },
  { value: 'ACCOUNTS_APPROVED', label: 'Accounts Approved' },
  { value: 'ACCOUNTS_REJECTED', label: 'Accounts Rejected' },
  { value: 'PAID', label: 'Paid' },
];

const PAYMENT_METHODS = [
  { value: 'all', label: 'All Methods' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'TRANSFER', label: 'Bank Transfer' },
  { value: 'SADAD', label: 'SADAD' },
];

const CURRENCIES = [
  { value: 'all', label: 'All Currencies' },
  { value: 'SAR', label: 'SAR' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
];

export const AdminVouchersPage = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15; // 15 items per page to show pagination

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vouchersData, deptsData, usersData] = await Promise.all([
        vouchersApi.getAll(),
        departmentsApi.getAll(),
        usersApi.getAll(),
      ]);
      setVouchers(vouchersData);
      setDepartments(deptsData);
      setEmployees(usersData.filter(u => u.role !== 'admin'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setEmployeeFilter('all');
    setPaymentMethodFilter('all');
    setCurrencyFilter('all');
    setDateFrom(null);
    setDateTo(null);
    setAmountMin('');
    setAmountMax('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const activeFiltersCount = [
    statusFilter !== 'all',
    departmentFilter !== 'all',
    employeeFilter !== 'all',
    paymentMethodFilter !== 'all',
    currencyFilter !== 'all',
    dateFrom !== null,
    dateTo !== null,
    amountMin !== '',
    amountMax !== '',
  ].filter(Boolean).length;

  const filteredVouchers = vouchers.filter((v) => {
    // Text search
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        v.beneficiaryName?.toLowerCase().includes(searchLower) ||
        v.voucherNo?.toLowerCase().includes(searchLower) ||
        v.poNumber?.toLowerCase().includes(searchLower) ||
        v.createdByName?.toLowerCase().includes(searchLower) ||
        v.department?.toLowerCase().includes(searchLower) ||
        v.branch?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;

    // Department filter
    if (departmentFilter !== 'all' && v.department !== departmentFilter) return false;

    // Employee filter
    if (employeeFilter !== 'all' && v.createdById !== employeeFilter) return false;

    // Payment method filter
    if (paymentMethodFilter !== 'all' && v.paymentMethod !== paymentMethodFilter) return false;

    // Currency filter
    if (currencyFilter !== 'all' && v.currency !== currencyFilter) return false;

    // Date range filter
    if (dateFrom) {
      const voucherDate = new Date(v.date);
      if (voucherDate < dateFrom) return false;
    }
    if (dateTo) {
      const voucherDate = new Date(v.date);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (voucherDate > toDate) return false;
    }

    // Amount range filter
    if (amountMin !== '' && v.amountTotal < parseFloat(amountMin)) return false;
    if (amountMax !== '' && v.amountTotal > parseFloat(amountMax)) return false;

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredVouchers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVouchers = filteredVouchers.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, departmentFilter, employeeFilter, paymentMethodFilter, currencyFilter, dateFrom, dateTo, amountMin, amountMax]);

  const exportToCSV = () => {
    const headers = ['Voucher No', 'PO Number', 'Date', 'Employee', 'Department', 'Beneficiary', 'Amount', 'Currency', 'Payment Method', 'Status'];
    
    // Helper function to escape CSV values
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    const rows = filteredVouchers.map(v => [
      escapeCSV(v.voucherNo || 'Draft'),
      escapeCSV(v.poNumber || ''),
      escapeCSV(formatDate(v.date)),
      escapeCSV(v.createdByName || ''),
      escapeCSV(v.department || ''),
      escapeCSV(v.beneficiaryName || ''),
      escapeCSV(v.amountTotal || 0),
      escapeCSV(v.currency || 'SAR'),
      escapeCSV(v.paymentMethod || ''),
      escapeCSV(v.status || '')
    ]);
    
    // Add UTF-8 BOM for Excel compatibility with special characters (Arabic, etc.)
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows.map(row => row.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vouchers_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Vouchers exported to CSV');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vouchers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">All Vouchers</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View and manage all vouchers in the system ({vouchers.length} total)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportToCSV}
          className="hidden sm:flex"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Main Search Row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by beneficiary, voucher no, PO number, employee, department..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showFilters ? "secondary" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#6a5da4] text-white text-xs rounded-full flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Department Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Department</Label>
                  <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                    <SelectTrigger data-testid="department-filter">
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Employee Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Created By</Label>
                  <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                    <SelectTrigger data-testid="employee-filter">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Method Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Payment Method</Label>
                  <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                    <SelectTrigger data-testid="payment-method-filter">
                      <SelectValue placeholder="All Methods" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Currency Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Currency</Label>
                  <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                    <SelectTrigger data-testid="currency-filter">
                      <SelectValue placeholder="All Currencies" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Date From */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Date From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="date-from-filter"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Date To */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Date To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="date-to-filter"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, 'PP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Amount Min */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Min Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amountMin}
                    onChange={(e) => setAmountMin(e.target.value)}
                    data-testid="amount-min-filter"
                  />
                </div>

                {/* Amount Max */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Max Amount</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={amountMax}
                    onChange={(e) => setAmountMax(e.target.value)}
                    data-testid="amount-max-filter"
                  />
                </div>
              </div>

              {/* Clear Filters Button */}
              {activeFiltersCount > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-muted-foreground"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Clear all filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vouchers Table */}
      <Card>
        <CardContent className="p-0">
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No vouchers found</h3>
              <p className="text-muted-foreground text-sm">
                {search || activeFiltersCount > 0 ? 'Try adjusting your filters' : 'No vouchers in the system'}
              </p>
              {activeFiltersCount > 0 && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Voucher No</TableHead>
                    <TableHead className="font-semibold">PO Number</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Employee</TableHead>
                    <TableHead className="font-semibold">Department</TableHead>
                    <TableHead className="font-semibold">Beneficiary</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedVouchers.map((voucher) => (
                    <TableRow
                      key={voucher.id}
                      className="hover:bg-gray-50"
                      data-testid={`voucher-row-${voucher.id}`}
                    >
                      <TableCell className="font-mono font-medium">
                        <div className="flex items-center gap-1">
                          {voucher.voucherNo || (
                            <span className="text-muted-foreground">Draft</span>
                          )}
                          {voucher.attachments && voucher.attachments.length > 0 && (
                            <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {voucher.poNumber || '-'}
                      </TableCell>
                      <TableCell>{formatDate(voucher.date)}</TableCell>
                      <TableCell>{voucher.createdByName}</TableCell>
                      <TableCell>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {voucher.department}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{voucher.beneficiaryName}</p>
                          <p className="text-xs text-muted-foreground">{voucher.branch}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        {formatCurrency(voucher.amountTotal, voucher.currency)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={voucher.status} size="sm" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/vouchers/${voucher.id}`)}
                          data-testid={`view-voucher-${voucher.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between mt-4 px-2 gap-4">
              <p className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredVouchers.length)} of {filteredVouchers.length} vouchers
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 1) return true;
                        return false;
                      })
                      .map((page, index, arr) => (
                        <React.Fragment key={page}>
                          {index > 0 && arr[index - 1] !== page - 1 && (
                            <span className="px-2 text-muted-foreground">...</span>
                          )}
                          <Button
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-[#6a5da4] hover:bg-[#5a4d94]' : ''}`}
                            onClick={() => setCurrentPage(page)}
                            data-testid={`pagination-page-${page}`}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    data-testid="pagination-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminVouchersPage;
